import { useQuery } from 'react-query';
import { useLocale } from '../../../../../../i18n';
import { Language } from '../../../../../../types/Common';
import { PartyType } from '../../../../../../types/SMT';
import { wgApi } from '../../../../../app/src/features/api/wgApi';
import {
  InsuranceProduct,
  InsuranceProductsResp,
} from '../types/InsuranceProducts';

export interface UseGetInsuranceProductsProps {
  page?: number;
  pagelen?: number;
  q?: string;
  userCountry?: string;
  userLang?: string;
  entity?: PartyType;
  riskDomain?: string;
  version?: string;
  lang?: Language;
}

const getInsuranceProducts = async ({
  userCountry,
  lang,
  entity,
  version = 'v1',
  riskDomain,
  q,
}: UseGetInsuranceProductsProps) => {
  const { data, isError } = await wgApi.get<InsuranceProductsResp>(
    `${version}/api/insurance-products`,
    {
      query: {
        country: userCountry,
        lang: lang,
        risk_domain: riskDomain,
        pagelen: 999,
        entity,
        q,
      },
    },
  );
  if (isError) throw new Error();
  return data;
};

export const getUseInsuranceProductsQKey = (
  filters?: UseGetInsuranceProductsProps,
) => ['settings', 'insurance-products', filters];

export const useInsuranceProducts = (
  params: UseGetInsuranceProductsProps = {},
  enabled = true,
) => {
  const { userCountry, userLang } = useLocale();

  return useQuery(
    getUseInsuranceProductsQKey(params),
    () =>
      getInsuranceProducts({
        userCountry,
        lang: params.lang || (userLang as Language),
        version: 'v2',
        ...params,
      }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled,
    },
  );
};

export const getInsuranceProduct = (
  insuranceProductId: string,
  products: InsuranceProduct[],
) => {
  const product = products.find(
    (product) => product.id === insuranceProductId,
  ) as InsuranceProduct;

  return product;
};

interface UseGetInsuranceProductProps {
  insuranceProductId: string;
  filters?: UseGetInsuranceProductsProps;
}

export const useGetInsuranceProduct = ({
  insuranceProductId,
  filters,
}: UseGetInsuranceProductProps): InsuranceProduct => {
  const { data } = useInsuranceProducts(filters);

  const product = getInsuranceProduct(insuranceProductId, data?.items || []);

  return product;
};
